import type {
  ShiftTimeline,
  ShiftTimelineCallsign,
  ShiftTimelineRequest,
} from '@transreport/pauk-rrms-api'
import { ShiftTimelineRequestConflictType } from '@transreport/pauk-rrms-api'
import { areIntervalsOverlapping, differenceInMinutes, parse } from 'date-fns'

import type {
  ScheduleConflict,
  ScheduleConflictsSummaryCounts,
  ScheduleConflictSeverity,
} from '@/components/modules/schedule-conflicts/types'
import { buildLinkedJobContexts, hasSharedBookingId } from '@/lib/linkedJobs'
import { extractTimeFromValue, resolveAssistanceTiming } from '@/lib/timeline/assistanceTiming'
import { UNASSIGNED_ROW_ID } from '@/lib/timeline/constants'
import type { CallsignRow, Request, RequestStatus } from '@/types/timeline'

/**
 * Canonical sort for requests by start_time, end_time, id.
 * Ensures optimistic updates and server refetches produce the same ordering,
 * which makes the layout algorithm deterministic and prevents lane churn.
 */
const sortRequests = (requests: ShiftTimelineRequest[]): ShiftTimelineRequest[] =>
  requests.toSorted((a, b) => {
    if (a.start_time !== b.start_time) return a.start_time < b.start_time ? -1 : 1
    if (a.end_time !== b.end_time) return a.end_time < b.end_time ? -1 : 1
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })

const ASSISTANCE_ITEM_LABEL_KEYS = [
  'humanized_value',
  'humanised_value',
  'label',
  'name',
  'value',
  'title',
] as const

const getAssistanceItemLabel = (item: unknown): string | undefined => {
  if (typeof item === 'string') {
    return item.trim() || undefined
  }

  if (!item || typeof item !== 'object') {
    return undefined
  }

  const record = item as Record<string, unknown>
  for (const key of ASSISTANCE_ITEM_LABEL_KEYS) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return undefined
}

const getAssistanceDetails = (
  assistanceItems?: ShiftTimelineRequest['assistance_items']
): string | undefined =>
  assistanceItems
    ?.map(getAssistanceItemLabel)
    .filter((label): label is string => Boolean(label))
    .join(', ') || undefined

// Show the "opposite" station in the job label: if the job's primary station
// (board for DEP, alight for ARR) matches the timeline's own station, display
// the other end of the journey instead so the label carries useful information.
const getStationCodeForLabel = (
  request: ShiftTimelineRequest,
  timelineStationCode?: string
): string | undefined => {
  const timelineCode = timelineStationCode

  const primaryCode =
    request.type === 'ARR' ? request.board_station?.crs_code : request.alight_station?.crs_code

  const fallbackCode =
    request.type === 'ARR' ? request.alight_station?.crs_code : request.board_station?.crs_code

  if (timelineCode && primaryCode === timelineCode && fallbackCode) {
    return fallbackCode
  }

  return primaryCode ?? fallbackCode
}

function toRequestStatus(apiRequest: ShiftTimelineRequest): RequestStatus {
  // Only treat explicit scheduling conflicts as CONFLICTING (matching the
  // staff app behaviour).  Other conflict_type values like 'out_of_hour' are
  // informational and should map to CAUTION instead.
  if (apiRequest.conflict_type === ShiftTimelineRequestConflictType.conflict) return 'CONFLICTING'
  if (
    apiRequest.conflict_type &&
    apiRequest.conflict_type !== ShiftTimelineRequestConflictType.no_conflict
  )
    return 'CAUTION'
  if (!apiRequest.assignee) return 'UNASSIGNED'
  return 'NO_CONFLICT'
}

const hasOverlappingLinkedRequest = (
  request: ShiftTimelineRequest,
  requests: ShiftTimelineRequest[]
): boolean => {
  const requestStart = timeStringToDate(request.start_time, new Date())
  const requestEnd = timeStringToDate(request.end_time, new Date())

  return requests.some((other) => {
    if (other.id === request.id || !hasSharedBookingId(request.booking.id, other.booking.id)) {
      return false
    }

    return areIntervalsOverlapping(
      { start: requestStart, end: requestEnd },
      {
        start: timeStringToDate(other.start_time, new Date()),
        end: timeStringToDate(other.end_time, new Date()),
      }
    )
  })
}

function toTimelineRequest(
  apiRequest: ShiftTimelineRequest,
  timelineStationCode?: string,
  options: ShiftTimelineToCallsignRowsOptions = {},
  suppressApiConflict = false
): Request {
  const {
    id,
    type,
    customer,
    scheduled_arrival,
    scheduled_departure,
    journey_leg,
    booking,
    start_time,
    end_time,
    additional_information,
    assistance_items,
    board_station,
    alight_station,
  } = apiRequest

  const customerName =
    [customer?.first_name, customer?.last_name].filter(Boolean).join(' ').trim() || undefined

  const scheduledTrainTime = type === 'DEP' ? scheduled_departure : scheduled_arrival
  const realtimeTrainTime =
    type === 'DEP' ? journey_leg.realtime_departure : journey_leg.realtime_arrival
  const resolvedTiming = resolveAssistanceTiming({
    type,
    startTime: start_time,
    endTime: end_time,
    scheduledTrainTime,
    realtimeTrainTime,
    autoUpdateAssistanceJobTime: options.autoUpdateAssistanceJobTime,
  })

  const stationCode = getStationCodeForLabel(apiRequest, timelineStationCode) ?? '---'
  const platform = type === 'DEP' ? journey_leg.board_platform : journey_leg.alight_platform

  // Prefer new booking summary fields, with deprecated fields as fallback.
  const bookingId = booking.id
  const bookingRef = booking.ref

  const request: Request = {
    id,
    type,
    label: `${type} - ${stationCode}`,
    customerName,
    scheduledTime: resolvedTiming.scheduledTime,
    assistanceAnchorTime: resolvedTiming.isRealtimeAdjusted
      ? resolvedTiming.realtimeTime
      : resolvedTiming.scheduledTime,
    scheduledDeparture: extractTimeFromValue(scheduled_departure) ?? undefined,
    scheduledArrival: extractTimeFromValue(scheduled_arrival) ?? undefined,
    startTime: resolvedTiming.startTime,
    endTime: resolvedTiming.endTime,
    status:
      suppressApiConflict && apiRequest.conflict_type === ShiftTimelineRequestConflictType.conflict
        ? 'NO_CONFLICT'
        : toRequestStatus(apiRequest),
    ref: bookingRef,
    bookingId,
    platform: platform ?? undefined,
    additionalInfo: additional_information ?? undefined,
    assistanceDetails: getAssistanceDetails(assistance_items),
    headCode: journey_leg.head_code ?? undefined,
    boardStationCode: board_station?.crs_code,
    alightStationCode: alight_station?.crs_code,
    boardStationName: board_station?.name,
    alightStationName: alight_station?.name,
    realtimeClassification: journey_leg.realtime_classification,
    realtimeDeparture: extractTimeFromValue(journey_leg.realtime_departure) ?? undefined,
    realtimeArrival: extractTimeFromValue(journey_leg.realtime_arrival) ?? undefined,
  }

  return request
}

function mapCallsignToRow(
  callsign: ShiftTimelineCallsign,
  timelineStationCode?: string,
  options: ShiftTimelineToCallsignRowsOptions = {}
): CallsignRow {
  const linkedConflictRequestIds = new Set(
    callsign.assigned_requests
      .filter((request) => hasOverlappingLinkedRequest(request, callsign.assigned_requests))
      .map((request) => request.id)
  )
  const hasConflict = callsign.assigned_requests.some(
    (request) =>
      request.conflict_type === ShiftTimelineRequestConflictType.conflict &&
      !linkedConflictRequestIds.has(request.id)
  )
  return {
    id: callsign.id,
    name: callsign.name,
    staffName: callsign.user_name ?? undefined,
    shiftStartTime: callsign.start_time,
    shiftEndTime: callsign.end_time,
    endsNextDay: callsign.ends_next_day,
    hasConflict: hasConflict || undefined,
    requests: callsign.assigned_requests.map((apiRequest) =>
      toTimelineRequest(
        apiRequest,
        timelineStationCode,
        options,
        linkedConflictRequestIds.has(apiRequest.id)
      )
    ),
  }
}

export interface ShiftTimelineToCallsignRowsOptions {
  autoUpdateAssistanceJobTime?: boolean
}

export function shiftTimelineToCallsignRows(
  data: ShiftTimeline | null,
  timelineStationCode?: string,
  options: ShiftTimelineToCallsignRowsOptions = {}
): CallsignRow[] {
  if (!data) return []

  const rows: CallsignRow[] = data.callsigns.map((callsign) =>
    mapCallsignToRow(callsign, timelineStationCode, options)
  )

  rows.push({
    id: UNASSIGNED_ROW_ID,
    name: 'Unassigned',
    shiftStartTime: '00:00',
    shiftEndTime: '23:59',
    endsNextDay: false,
    requests: data.unassigned_requests.map((apiRequest) =>
      toTimelineRequest(apiRequest, timelineStationCode, options)
    ),
  })

  const linkedJobContexts = buildLinkedJobContexts(
    rows.flatMap((row) =>
      row.requests.map((request) => ({
        id: request.id,
        bookingId: request.bookingId,
        type: request.type,
        startTime: request.startTime,
        endTime: request.endTime,
        fromCode: request.boardStationCode,
        toCode: request.alightStationCode,
      }))
    )
  )

  return rows.map((row) => ({
    ...row,
    requests: row.requests.map((request) => ({
      ...request,
      linkedJobContext: linkedJobContexts.get(request.id),
    })),
  }))
}

function timeStringToDate(time: string, referenceDate: Date): Date {
  return parse(time, 'HH:mm', referenceDate)
}

function getMinutesDifference(a: string, b: string, referenceDate: Date): number {
  const aDate = timeStringToDate(a, referenceDate)
  const bDate = timeStringToDate(b, referenceDate)
  return Math.abs(differenceInMinutes(aDate, bDate))
}

function classifyConflictSeverity(
  diffMinutes: number,
  overlapsInTime: boolean
): ScheduleConflictSeverity | null {
  if (overlapsInTime || diffMinutes <= 15) {
    return 'critical'
  }
  if (diffMinutes > 15 && diffMinutes <= 20) {
    return 'high'
  }
  return null
}

export function computeScheduleConflicts(
  data: ShiftTimeline | null,
  callsignRows: CallsignRow[],
  referenceDate: Date
): {
  conflicts: ScheduleConflict[]
  counts: ScheduleConflictsSummaryCounts
} {
  if (!data) {
    return {
      conflicts: [],
      counts: { critical: 0, high: 0, moderate: 0 },
    }
  }

  const conflicts: ScheduleConflict[] = []
  const counts: ScheduleConflictsSummaryCounts = {
    critical: 0,
    high: 0,
    moderate: 0,
  }
  const requestSeverityByCallsignId = new Map<string, Map<string, ScheduleConflictSeverity>>()
  const callsignIdsWithConflicts = new Set<string>()

  data.callsigns.forEach((callsign) => {
    const requests = callsign.assigned_requests
    if (requests.length < 2) return

    for (let i = 0; i < requests.length; i++) {
      for (let j = i + 1; j < requests.length; j++) {
        const a = requests[i]
        const b = requests[j]

        const aStart = a.start_time
        const aEnd = a.end_time
        const bStart = b.start_time
        const bEnd = b.end_time

        const intervalA = {
          start: timeStringToDate(aStart, referenceDate),
          end: timeStringToDate(aEnd, referenceDate),
        }
        const intervalB = {
          start: timeStringToDate(bStart, referenceDate),
          end: timeStringToDate(bEnd, referenceDate),
        }
        const overlaps = areIntervalsOverlapping(intervalA, intervalB)

        const diffStart = getMinutesDifference(aStart, bStart, referenceDate)
        if (overlaps && hasSharedBookingId(a.booking.id, b.booking.id)) continue

        const severity = classifyConflictSeverity(diffStart, overlaps)

        if (!severity) continue

        callsignIdsWithConflicts.add(callsign.id)
        counts[severity] += 1

        conflicts.push({
          id: `${callsign.id}-${a.id}-${b.id}`,
          severity,
          status: 'unresolved',
          serviceCode: callsign.name,
          bookingRef: a.booking.ref,
          timingSummary: `${aStart}–${aEnd}`,
          descriptionKey: 'scheduleConflicts.timingConflictWith',
          description: 'Timing conflict with',
          conflictingServiceCode: callsign.name,
          conflictingBookingRef: b.booking.ref,
          conflictingTimingSummary: `${bStart}–${bEnd}`,
        })

        const setRequestSeverity = (reqId: string, sev: ScheduleConflictSeverity) => {
          let map = requestSeverityByCallsignId.get(callsign.id)
          if (!map) {
            map = new Map()
            requestSeverityByCallsignId.set(callsign.id, map)
          }
          const existing = map.get(reqId)
          if (!existing || (sev === 'critical' && existing !== 'critical')) {
            map.set(reqId, sev)
          }
        }
        setRequestSeverity(a.id, severity)
        setRequestSeverity(b.id, severity)
      }
    }
  })

  callsignRows.forEach((row) => {
    if (callsignIdsWithConflicts.has(row.id)) {
      row.hasConflict = true
    }
    const severityMap = requestSeverityByCallsignId.get(row.id)
    if (severityMap) {
      row.requests.forEach((request) => {
        const sev = severityMap.get(request.id)
        if (sev === 'critical') request.status = 'CONFLICTING'
        else if (sev === 'high') request.status = 'CAUTION'
      })
    }
  })

  return { conflicts, counts }
}

type RequestUpdate = {
  startTime?: string
  endTime?: string
  newCallsignId?: string
}

type RequestLocation =
  | { kind: 'callsign'; callsignIndex: number; requestIndex: number }
  | { kind: 'unassigned'; requestIndex: number }

function findRequestLocation(data: ShiftTimeline, requestId: string): RequestLocation | null {
  for (let i = 0; i < data.callsigns.length; i++) {
    const requestIndex = data.callsigns[i].assigned_requests.findIndex(
      (request) => request.id === requestId
    )
    if (requestIndex !== -1) {
      return { kind: 'callsign', callsignIndex: i, requestIndex }
    }
  }

  const unassignedIndex = data.unassigned_requests.findIndex((request) => request.id === requestId)
  if (unassignedIndex !== -1) {
    return { kind: 'unassigned', requestIndex: unassignedIndex }
  }

  return null
}

const getRequestAtLocation = (
  data: ShiftTimeline,
  location: RequestLocation
): ShiftTimelineRequest =>
  location.kind === 'callsign'
    ? data.callsigns[location.callsignIndex].assigned_requests[location.requestIndex]
    : data.unassigned_requests[location.requestIndex]

const buildUpdatedRequest = (
  request: ShiftTimelineRequest,
  { startTime, endTime }: RequestUpdate
): ShiftTimelineRequest => ({
  ...request,
  ...(startTime != null && { start_time: startTime }),
  ...(endTime != null && { end_time: endTime }),
})

const moveBetweenCallsigns = (
  data: ShiftTimeline,
  requestId: string,
  updatedRequest: ShiftTimelineRequest,
  sourceIndex: number,
  targetId: string
): ShiftTimeline => {
  const targetIndex = data.callsigns.findIndex((callsign) => callsign.id === targetId)
  if (targetIndex === -1 || targetIndex === sourceIndex) return data

  const callsigns = data.callsigns.map((callsign, index) => {
    if (index === sourceIndex) {
      return {
        ...callsign,
        assigned_requests: callsign.assigned_requests.filter((r) => r.id !== requestId),
      }
    }

    if (index === targetIndex) {
      return {
        ...callsign,
        assigned_requests: sortRequests([...callsign.assigned_requests, updatedRequest]),
      }
    }

    return callsign
  })

  return { ...data, callsigns }
}

const moveFromUnassignedToCallsign = (
  data: ShiftTimeline,
  requestId: string,
  updatedRequest: ShiftTimelineRequest,
  targetId: string
): ShiftTimeline => {
  const targetIndex = data.callsigns.findIndex((callsign) => callsign.id === targetId)
  if (targetIndex === -1) return data

  const callsigns = data.callsigns.map((callsign, index) =>
    index === targetIndex
      ? {
          ...callsign,
          assigned_requests: sortRequests([...callsign.assigned_requests, updatedRequest]),
        }
      : callsign
  )

  const unassigned_requests = data.unassigned_requests.filter((r) => r.id !== requestId)
  return { ...data, callsigns, unassigned_requests }
}

const moveFromCallsignToUnassigned = (
  data: ShiftTimeline,
  requestId: string,
  updatedRequest: ShiftTimelineRequest,
  sourceIndex: number
): ShiftTimeline => {
  const callsigns = data.callsigns.map((callsign, index) =>
    index === sourceIndex
      ? {
          ...callsign,
          assigned_requests: callsign.assigned_requests.filter((r) => r.id !== requestId),
        }
      : callsign
  )

  return {
    ...data,
    callsigns,
    unassigned_requests: sortRequests([...data.unassigned_requests, updatedRequest]),
  }
}

const updateInCallsign = (
  data: ShiftTimeline,
  requestId: string,
  location: Extract<RequestLocation, { kind: 'callsign' }>,
  updatedRequest: ShiftTimelineRequest
): ShiftTimeline => {
  const callsigns = data.callsigns.map((callsign, index) =>
    index === location.callsignIndex
      ? {
          ...callsign,
          assigned_requests: sortRequests(
            callsign.assigned_requests.map((r) => (r.id === requestId ? updatedRequest : r))
          ),
        }
      : callsign
  )

  return { ...data, callsigns }
}

const updateInUnassigned = (
  data: ShiftTimeline,
  requestId: string,
  updatedRequest: ShiftTimelineRequest
): ShiftTimeline => {
  const unassigned_requests = sortRequests(
    data.unassigned_requests.map((r) => (r.id === requestId ? updatedRequest : r))
  )

  return { ...data, unassigned_requests }
}

export function applyRequestUpdate(
  data: ShiftTimeline,
  requestId: string,
  updates: RequestUpdate
): ShiftTimeline {
  const location = findRequestLocation(data, requestId)
  if (!location) return data

  const apiRequest = getRequestAtLocation(data, location)
  const updatedRequest = buildUpdatedRequest(apiRequest, updates)

  if (updates.newCallsignId) {
    if (updates.newCallsignId === UNASSIGNED_ROW_ID) {
      return location.kind === 'callsign'
        ? moveFromCallsignToUnassigned(data, requestId, updatedRequest, location.callsignIndex)
        : data
    }

    return location.kind === 'callsign'
      ? moveBetweenCallsigns(
          data,
          requestId,
          updatedRequest,
          location.callsignIndex,
          updates.newCallsignId
        )
      : moveFromUnassignedToCallsign(data, requestId, updatedRequest, updates.newCallsignId)
  }

  return location.kind === 'callsign'
    ? updateInCallsign(data, requestId, location, updatedRequest)
    : updateInUnassigned(data, requestId, updatedRequest)
}
